import * as React from 'react'

import Localization from '../../localization/LocalizationManager'
import ResourceControlsTab from '../ResourceControls/ResourceControlsTab'
import StruxTagSessionLog from '../StruxTagActivityLog/StruxTagSessionLog'
import StruxTagUtilizationView from '../StruxTagActivityLog/StruxTagUtilizationView'
import { ResourceEdit } from './ResourceEdit'
import { IResourceEditSection } from './ResourceEditSections'

/** The resource properties (form) section — the record's editable fields. */
export function generalResourceSection(): IResourceEditSection {
  return {
    id: 'general',
    label: Localization.translator.resourceProperties,
    content: <ResourceEdit.Form />,
    // The footer Save submits the form living inside this section, so keep it
    // mounted even when collapsed.
    keepMounted: true,
  }
}

/**
 * Mark exactly one section (by id) as expanded-by-default and collapse the rest,
 * so each entry point can choose which section opens first: Resource Properties
 * from the objects table, Resource Controls from the controls table, Map
 * Properties from the maps view.
 */
export function withDefaultExpanded(
  sections: IResourceEditSection[],
  expandedSectionId: string,
): IResourceEditSection[] {
  return sections.map(section => ({
    ...section,
    defaultExpanded: section.id === expandedSectionId,
  }))
}

/**
 * The Workflow-Resource controls section (equipment, materials). Its edits
 * update a draft held by the enclosing ResourceControlsTabProvider; the shared
 * footer Save commits that draft via context, so this section can unmount /
 * remount when collapsed without losing state (it is not keepMounted). Requires
 * the enclosing body to wrap the card in a ResourceControlsTabProvider, and a
 * saved record.
 */
export function controlsSection(): IResourceEditSection {
  return {
    id: 'controls',
    label: Localization.translator.resourceControls,
    content: <ResourceControlsTab />,
    requiresSavedRecord: true,
  }
}

interface IStruxTagTelemetryArgs {
  projectId?: string
  infuseId?: string | null
}

/**
 * StruxTag telemetry sections — Utilization + Sessions, both requiring a saved
 * record. Shown only when the StruxTag app is enabled for the project.
 */
export function struxTagTelemetrySections({
  projectId,
  infuseId,
}: IStruxTagTelemetryArgs): IResourceEditSection[] {
  return [
    {
      id: 'utilization',
      label: Localization.translator.utilization,
      content: (
        <StruxTagUtilizationView projectId={projectId} infuseId={infuseId} />
      ),
      requiresSavedRecord: true,
    },
    {
      id: 'sessions',
      label: Localization.translator.sessions,
      content: <StruxTagSessionLog projectId={projectId} infuseId={infuseId} />,
      requiresSavedRecord: true,
    },
  ]
}

interface IResourceTagSectionsArgs extends IStruxTagTelemetryArgs {
  /**
   * Include the StruxTag-only Utilization + Sessions sections. Defaults to true;
   * callers pass false to hide them when the StruxTag app is turned off.
   */
  showStruxTagSections?: boolean
}

/** Details sections for StruxTag / linked-resource cards: properties + utilization + sessions. */
export function buildResourceTagSections({
  projectId,
  infuseId,
  showStruxTagSections = true,
}: IResourceTagSectionsArgs): IResourceEditSection[] {
  return [
    generalResourceSection(),
    ...(showStruxTagSections
      ? struxTagTelemetrySections({ projectId, infuseId })
      : []),
  ]
}
